/* FLM — BlurHash blur-up support. The backend ships a tiny BlurHash string per
   photo; we decode it to a small canvas data URL used as an instant placeholder
   while the full image downloads (and fades in over it).

   A url→hash registry lets GradPhoto resolve the placeholder from just the photo
   URL it already receives — so callers don't have to thread the hash through
   every layer. Decoded data URLs are cached per hash (decode is cheap but not
   free, and the same cover repeats across the deck/lists). */
import { decode } from 'blurhash';

const REG = new Map(); // photo URL -> blurhash string
const CACHE = new Map(); // blurhash -> data URL (or null if undecodable)

/** Pair a photo URL with its BlurHash (called by the API mappers as data loads). */
export function registerBlur(url, hash) {
  if (url && hash && !REG.has(url)) REG.set(url, hash);
}

/** BlurHash for a photo URL, if one was registered. */
export function blurFor(url) {
  return url ? REG.get(url) : undefined;
}

/** Decode a BlurHash to a 32×32 PNG data URL (cached). Null if it can't decode. */
export function blurDataUrl(hash) {
  if (!hash) return null;
  if (CACHE.has(hash)) return CACHE.get(hash);
  let url = null;
  try {
    const W = 32;
    const H = 32;
    const pixels = decode(hash, W, H); // Uint8ClampedArray, RGBA
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    const imageData = ctx.createImageData(W, H);
    imageData.data.set(pixels);
    ctx.putImageData(imageData, 0, 0);
    url = canvas.toDataURL();
  } catch {
    url = null; // bad hash / no canvas — fall back to the gradient placeholder
  }
  CACHE.set(hash, url);
  return url;
}
